import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CreditCard, MapPin, ArrowRight, Loader2, Check, AlertCircle, Building2, ChevronsUpDown, Shield, Banknote, Copy, ChevronLeft, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { useCart } from '@/lib/cart-context';
import { useAuth } from '@/lib/auth-context';
import { useCurrency } from '@/lib/currency-context';
import { orders, payments, products } from '@/lib/api';
import { computeCartTaxFromItems, productPriceCurrency } from '@/lib/productTax';
import { buildGuestOrderTrackPath, saveGuestCheckoutContext, readGuestCheckoutContext } from '@/lib/guest-order-track';
import { savePendingCheckout, readPendingCheckout, clearPendingCheckout } from '@/lib/pending-checkout';
import { getCartShippingTRY, subtotalToTRY, guessShippingDestination } from '@/lib/shippingQuote';
import { applyProductTranslation, getLocalizedProductName, getProductLangKey } from '@/lib/product-i18n';
import { countryFlagEmoji, getLocalizedCountryName, normalizeCountryCode } from '@/lib/country-i18n';
import { toast } from 'sonner';
import PaymentBrandLogos from '@/components/payment/PaymentBrandLogos';
import { BANK_TRANSFER_DETAILS } from '@/lib/payment-methods';
import { trackAiEvent } from '@/lib/ai-search-track';
import {
  CheckoutSummaryBreakdown,
  CheckoutStickyBar,
  CheckoutDesktopSidebar,
  OrangeRadio,
  CheckoutSectionBand,
  CheckoutLegalConsent,
} from '@/components/checkout/CheckoutSummaryPanel';

// Tüm ülkeler listesi
/** Kod listesi — adlar aktif dile göre getLocalizedCountryName ile üretilir */
const ALL_COUNTRY_CODES = [
  'TR', 'DE', 'US', 'GB', 'FR', 'NL', 'BE', 'AT', 'CH', 'IT', 'ES', 'SE', 'NO', 'DK', 'FI',
  'PL', 'CZ', 'HU', 'RO', 'BG', 'GR', 'PT', 'IE', 'RU', 'UA', 'AZ', 'KZ', 'GE', 'AM', 'CA',
  'MX', 'BR', 'AR', 'AU', 'NZ', 'JP', 'KR', 'CN', 'IN', 'SA', 'AE', 'EG', 'ZA', 'NG', 'MA',
  'TN', 'SG', 'MY', 'TH', 'VN', 'ID', 'PH', 'PK', 'BD', 'HR', 'RS', 'SI', 'SK', 'LT', 'LV',
  'EE', 'CY', 'MT', 'LU', 'IS', 'AL', 'BA', 'MK', 'ME', 'XK', 'MD', 'BY', 'CL', 'CO', 'PE',
  'VE', 'EC', 'UY', 'PY', 'BO', 'CR', 'PA', 'DO', 'CU', 'PR', 'JM', 'HK', 'TW', 'IQ', 'IR',
  'SY', 'JO', 'LB', 'KW', 'QA', 'BH', 'OM', 'YE', 'AF', 'NP', 'LK', 'MM', 'KH', 'LA', 'MN',
  'KE', 'ET', 'TZ', 'UG', 'GH', 'CI', 'SN', 'CM', 'DZ', 'LY', 'SD',
];

// Türkiye illeri
const TURKEY_CITIES = [
  'Adana', 'Adıyaman', 'Afyonkarahisar', 'Ağrı', 'Aksaray', 'Amasya', 'Ankara', 'Antalya',
  'Ardahan', 'Artvin', 'Aydın', 'Balıkesir', 'Bartın', 'Batman', 'Bayburt', 'Bilecik',
  'Bingöl', 'Bitlis', 'Bolu', 'Burdur', 'Bursa', 'Çanakkale', 'Çankırı', 'Çorum',
  'Denizli', 'Diyarbakır', 'Düzce', 'Edirne', 'Elazığ', 'Erzincan', 'Erzurum', 'Eskişehir',
  'Gaziantep', 'Giresun', 'Gümüşhane', 'Hakkari', 'Hatay', 'Iğdır', 'Isparta', 'İstanbul',
  'İzmir', 'Kahramanmaraş', 'Karabük', 'Karaman', 'Kars', 'Kastamonu', 'Kayseri', 'Kırıkkale',
  'Kırklareli', 'Kırşehir', 'Kilis', 'Kocaeli', 'Konya', 'Kütahya', 'Malatya', 'Manisa',
  'Mardin', 'Mersin', 'Muğla', 'Muş', 'Nevşehir', 'Niğde', 'Ordu', 'Osmaniye', 'Rize',
  'Sakarya', 'Samsun', 'Siirt', 'Sinop', 'Sivas', 'Şanlıurfa', 'Şırnak', 'Tekirdağ',
  'Tokat', 'Trabzon', 'Tunceli', 'Uşak', 'Van', 'Yalova', 'Yozgat', 'Zonguldak'
];

const CheckoutPage = () => {
  const { t, i18n } = useTranslation();
  const { items, clearCart, restoreCartItems, getWholesalePrice, loading: cartLoading } = useCart();
  const [cartRestoreDone, setCartRestoreDone] = useState(false);
  const { user } = useAuth();
  const { formatPrice, convertPrice, currency, exchangeRates, countryCode } = useCurrency();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  // Guest checkout state
  const [isGuestCheckout, setIsGuestCheckout] = useState(false);
  const [guestInfo, setGuestInfo] = useState({
    email: '',
    phone: ''
  });
  
  const [address, setAddress] = useState({
    full_name: user?.name || '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: ''  // Boş başlasın, kullanıcı seçsin
  });
  
  const [countryOpen, setCountryOpen] = useState(false);
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const [enrichedItems, setEnrichedItems] = useState([]);
  const [ziraatCard, setZiraatCard] = useState({
    holder: '',
    number: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    installment: '1'
  });
  const [paymentMethod, setPaymentMethod] = useState('card'); // card | bank_transfer
  const [bankTransferOrder, setBankTransferOrder] = useState(null);
  const [editingAddress, setEditingAddress] = useState(true);
  const [savingsOpen, setSavingsOpen] = useState(true);
  const [mobileSummaryOpen, setMobileSummaryOpen] = useState(true);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const addressAutoCollapsed = useRef(false);

  // Adım / ekran değişince sayfayı üste al (adres → ödeme sonrası alta takılmasın)
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [step, bankTransferOrder]);

  const localizedCountries = useMemo(() => {
    const lang = i18n.language;
    const list = ALL_COUNTRY_CODES.map((code) => ({
      code,
      flag: countryFlagEmoji(code),
      name: getLocalizedCountryName(code, lang),
    }));
    return list.sort((a, b) => a.name.localeCompare(b.name, lang || 'en'));
  }, [i18n.language]);

  const selectedCountry = useMemo(
    () => localizedCountries.find((c) => c.code === address.country) || null,
    [localizedCountries, address.country],
  );

  // Başarısız kart ödemesi sonrası sepet/adres geri yükle (yalnızca bir kez)
  const didRestorePendingRef = useRef(false);
  useEffect(() => {
    if (didRestorePendingRef.current || cartLoading) return undefined;
    didRestorePendingRef.current = true;
    let cancelled = false;
    const restore = async () => {
      try {
        const pending = readPendingCheckout();
        if (!pending?.items?.length) {
          if (!cancelled) setCartRestoreDone(true);
          return;
        }
        if (pending.address && typeof pending.address === 'object') {
          setAddress((prev) => ({ ...prev, ...pending.address }));
        }
        if (pending.guestInfo?.email) {
          setGuestInfo((prev) => ({ ...prev, ...pending.guestInfo }));
          setIsGuestCheckout(true);
        }
        // Sepet zaten doluysa sadece adres/guest bilgisi yeter; tekrar clear+add yok
        if (!items.length) {
          await restoreCartItems(pending.items);
        }
      } finally {
        if (!cancelled) setCartRestoreDone(true);
      }
    };
    restore();
    return () => { cancelled = true; };
    // items/restoreCartItems bilerek bağımlılıkta değil — tek seferlik mount restore
  }, [cartLoading]);

  // Kayıtlı kullanıcının varsayılan adresini ödeme formuna yükle
  useEffect(() => {
    if (!user) return;
    const pending = readPendingCheckout();
    if (pending?.address?.address_line1) return; // pending checkout öncelikli
    const scrubBlockedCountry = (c) => {
      const s = String(c || '').trim();
      if (!s) return '';
      if (s.toUpperCase() === 'IL' || /israel|israil|İsrail/i.test(s)) return '';
      return s;
    };
    const deliveryGuess = guessShippingDestination({ currency, countryCode });
    const isSellerAccount = user.role === 'seller';
    const profileCountry = isSellerAccount ? '' : (user.country || '');
    const raw = user.address;
    if (raw && typeof raw === 'object' && raw.address_line1) {
      setAddress({
        full_name: raw.full_name || user.name || '',
        phone: raw.phone || user.phone || '',
        address_line1: raw.address_line1 || '',
        address_line2: raw.address_line2 || '',
        city: raw.city || user.city || '',
        state: raw.state || '',
        postal_code: raw.postal_code || user.postal_code || '',
        country: isSellerAccount
          ? deliveryGuess
          : (scrubBlockedCountry(raw.country || profileCountry) || deliveryGuess),
      });
      return;
    }
    if (user.full_address || user.phone) {
      setAddress((prev) => ({
        ...prev,
        full_name: user.name || prev.full_name,
        phone: user.phone || prev.phone,
        address_line1: user.full_address || prev.address_line1,
        city: user.city || prev.city,
        postal_code: user.postal_code || prev.postal_code,
        country: isSellerAccount
          ? deliveryGuess
          : (scrubBlockedCountry(profileCountry || prev.country) || deliveryGuess),
      }));
    } else {
      setAddress((prev) => (
        prev.country ? prev : { ...prev, country: deliveryGuess }
      ));
    }
  }, [user]);

  useEffect(() => {
    if (addressAutoCollapsed.current) return;
    if (address.full_name && address.address_line1 && address.city && address.phone && address.country) {
      setEditingAddress(false);
      addressAutoCollapsed.current = true;
    }
  }, [address.full_name, address.address_line1, address.city, address.phone, address.country]);

  const productLangKey = getProductLangKey(i18n.language);

  // Sepetteki ürünlerin güncel verilerini API'den çek (+ aktif dil çevirisi)
  useEffect(() => {
    const fetchProductDetails = async () => {
      if (items.length === 0) {
        setEnrichedItems([]);
        return;
      }

      const withLocale = (product) => {
        const merged = {
          ...product,
          seller_country:
            normalizeCountryCode(product?.seller_country) || 'TR',
        };
        // Çeviriyi uygula ama orijinal adı sakla (sipariş kaydı için)
        const localized = applyProductTranslation(merged, productLangKey);
        return {
          ...localized,
          original_name: merged.name,
          translations: merged.translations || product?.translations,
        };
      };
      
      try {
        // Her ürün için güncel veriyi çek
        const enriched = await Promise.all(
          items.map(async (item) => {
            try {
              const response = await products.get(item.product_id);
              const freshProduct = response.data;
              return {
                ...item,
                product: withLocale({
                  ...item.product,
                  ...freshProduct,
                  seller_country:
                    normalizeCountryCode(freshProduct.seller_country) ||
                    normalizeCountryCode(item.product?.seller_country) ||
                    'TR',
                }),
              };
            } catch (err) {
              console.log('Could not fetch product:', item.product_id);
              return {
                ...item,
                product: withLocale({
                  ...item.product,
                  seller_country:
                    normalizeCountryCode(item.product?.seller_country) || 'TR',
                }),
              };
            }
          })
        );
        setEnrichedItems(enriched);
      } catch (error) {
        console.error('Failed to enrich cart items:', error);
        // Fallback - use existing items with normalized country codes
        setEnrichedItems(items.map((item) => ({
          ...item,
          product: withLocale({
            ...item.product,
            seller_country:
              normalizeCountryCode(item.product?.seller_country) || 'TR',
          }),
        })));
      }
    };
    
    fetchProductDetails();
  }, [items, productLangKey]);

  // Analyze cart items by seller country - enrichedItems kullan
  const cartAnalysis = useMemo(() => {
    const turkishSellerItems = [];
    const internationalSellerItems = [];
    let turkishTotal = 0;
    let internationalTotal = 0;

    // enrichedItems yoksa items kullan
    const itemsToAnalyze = enrichedItems.length > 0 ? enrichedItems : items;

    itemsToAnalyze.forEach(item => {
      // Normalize edilmiş seller_country kullan, varsayılan TR (Türk pazaryeri)
      const rawCountry = item.product?.seller_country;
      const sellerCountry = normalizeCountryCode(rawCountry) || 'TR';
      const itemTotal = (item.product?.price || 0) * item.quantity;
      
      if (sellerCountry === 'TR') {
        turkishSellerItems.push(item);
        turkishTotal += itemTotal;
      } else {
        internationalSellerItems.push(item);
        internationalTotal += itemTotal;
      }
    });

    return {
      turkishSellerItems,
      internationalSellerItems,
      turkishTotal,
      internationalTotal,
      hasTurkishProducts: turkishSellerItems.length > 0,
      hasInternationalProducts: internationalSellerItems.length > 0,
      onlyTurkishProducts: turkishSellerItems.length > 0 && internationalSellerItems.length === 0,
      onlyInternationalProducts: internationalSellerItems.length === 0 && turkishSellerItems.length === 0,
      mixedCart: turkishSellerItems.length > 0 && internationalSellerItems.length > 0
    };
  }, [items, enrichedItems]);

  // Check for payment success
  const sessionId = searchParams.get('session_id');
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    if (paymentStatus === 'success') trackAiEvent('purchase');
  }, [paymentStatus]);

  const pollPaymentStatus = React.useCallback(async (sid, attempts = 0) => {
    if (attempts >= 5) {
      setPaymentStatus('timeout');
      return;
    }

    try {
      const response = await payments.status(sid);
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('success');
        clearPendingCheckout();
        clearCart();
      } else if (response.data.status === 'expired') {
        setPaymentStatus('failed');
      } else {
        setTimeout(() => {
          // Recursive call with incremented attempts
          const pollNext = async () => {
            const res = await payments.status(sid);
            if (res.data.payment_status === 'paid') {
              setPaymentStatus('success');
              clearPendingCheckout();
              clearCart();
            } else if (res.data.status === 'expired' || attempts + 1 >= 5) {
              setPaymentStatus(res.data.status === 'expired' ? 'failed' : 'timeout');
            } else {
              setTimeout(() => pollNext(), 2000);
            }
          };
          pollNext().catch(() => setPaymentStatus('error'));
        }, 2000);
      }
    } catch (error) {
      console.error('Payment status error:', error);
      setPaymentStatus('error');
    }
  }, [clearCart]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId, pollPaymentStatus]);

  const handleAddressChange = (field, value) => {
    setAddress(prev => ({ ...prev, [field]: value }));
  };

  const handleGuestInfoChange = (field, value) => {
    setGuestInfo(prev => ({ ...prev, [field]: value }));
  };

  const validateGuestInfo = () => {
    if (!guestInfo.email) {
      toast.error(t('checkout.emailRequired'));
      return false;
    }
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(guestInfo.email)) {
      toast.error(t('checkout.invalidEmail'));
      return false;
    }
    // Cep telefonu zorunlu kontrolü
    if (!guestInfo.phone) {
      toast.error(t('checkout.phoneRequired'));
      return false;
    }
    const phoneClean = guestInfo.phone.replace(/\D/g, '');
    if (phoneClean.length < 10) {
      toast.error(t('checkout.invalidPhone'));
      return false;
    }
    return true;
  };

  // Adres formu validasyonu
  const validateAddressForm = () => {
    if (!address.country) {
      toast.error(t('checkout.selectCountryRequired'));
      return false;
    }
    const countryNorm = String(address.country || '').trim().toUpperCase();
    if (countryNorm === 'IL' || /israel|israil|İsrail/i.test(String(address.country || ''))) {
      toast.error(
        t(
          'checkout.shippingCountryBlocked',
          'Bu ülkeye gönderim yapılmamaktadır. / We do not ship to this country.'
        )
      );
      return false;
    }
    if (!address.full_name) {
      toast.error(t('checkout.fullNameRequired'));
      return false;
    }
    if (!address.phone) {
      toast.error(t('checkout.phoneRequired'));
      return false;
    }
    const phoneClean = address.phone.replace(/\D/g, '');
    if (phoneClean.length < 10) {
      toast.error(t('checkout.invalidPhone'));
      return false;
    }
    if (!address.address_line1) {
      toast.error(t('checkout.addressRequired'));
      return false;
    }
    if (!address.city) {
      toast.error(t('checkout.cityRequired'));
      return false;
    }
    return true;
  };

  const handleSubmitOrder = async () => {
    // For guest checkout, validate guest info
    if (!user && isGuestCheckout) {
      if (!validateGuestInfo()) return;
    } else if (!user && !isGuestCheckout) {
      // Show guest/login choice if not already chosen
      return;
    }

    // Adres formu validasyonu - her durumda zorunlu
    if (!validateAddressForm()) return;

    setLoading(true);
    try {
      // Create order
      const orderSource = enrichedItems.length > 0 ? enrichedItems : items;
      const orderItems = orderSource.map((item) => ({
        product_id: item.product_id,
        // Sipariş kaydında orijinal ad; UI çevirisi ayrı
        product_name:
          item.product?.original_name
          || item.product?.name
          || item.name
          || 'Urun',
        price: item.product?.price || item.price || 0,
        quantity: item.quantity || 1,
        image: item.product?.images?.[0] || item.image || null,
        selected_variant: item.selected_variant || null,
      }));

      let orderResponse;
      let paymentResponse;

      // Check if this is a Turkish-only cart (Ziraat payment or Havale/EFT)
      if (cartAnalysis.onlyTurkishProducts) {
        const checkoutMeta = {
          // currency = müşteri görüntü para birimi; tutarlar/kargo backend'de TRY saklanır
          currency: currency || 'TRY',
          checkout_locale: (i18n.language || 'tr').split('-')[0],
          checkout_shipping: shippingCost, // TRY
        };

        if (paymentMethod === 'bank_transfer') {
          if (user) {
            orderResponse = await orders.create({
              items: orderItems,
              shipping_address: address,
              payment_method: 'bank_transfer',
              ...checkoutMeta,
            });
          } else {
            orderResponse = await orders.createGuest({
              items: orderItems,
              shipping_address: address,
              guest_email: guestInfo.email,
              guest_phone: guestInfo.phone || null,
              payment_method: 'bank_transfer',
              ...checkoutMeta,
            });
          }

          const created = user ? orderResponse.data : orderResponse.data.order;
          if (!user) {
            clearCart();
            saveGuestCheckoutContext({
              orderNumber: created.order_number || created.id,
              email: guestInfo.email,
            });
          }
          setBankTransferOrder({
            orderNumber: created.order_number,
            total: created.total,
            currency: created.currency || currency || 'TRY',
          });
          setLoading(false);
          return;
        }

        // Validate Ziraat card fields
        if (!ziraatCard.holder || !ziraatCard.number || !ziraatCard.expiryMonth || !ziraatCard.expiryYear || !ziraatCard.cvv) {
          toast.error(t('checkout.fillAllCardFields'));
          setLoading(false);
          return;
        }

        // Aynı oturumda başarısız ödemeden sonra yeni sipariş açma — mevcut bekleyen siparişi kullan
        const pending = readPendingCheckout();
        let createdOrder = null;
        if (
          pending?.orderId
          && pending?.paymentPending
          && pending?.total != null
          && Array.isArray(pending.items)
          && pending.items.length === orderItems.length
        ) {
          createdOrder = {
            id: pending.orderId,
            order_number: pending.orderNumber,
            total: pending.total,
            currency: pending.currency || currency || 'TRY',
          };
        } else {
          if (user) {
            orderResponse = await orders.create({
              items: orderItems,
              shipping_address: address,
              payment_method: 'card',
              ...checkoutMeta,
            });
          } else {
            orderResponse = await orders.createGuest({
              items: orderItems,
              shipping_address: address,
              guest_email: guestInfo.email,
              guest_phone: guestInfo.phone || null,
              payment_method: 'card',
              ...checkoutMeta,
            });
          }
          createdOrder = user ? orderResponse.data : orderResponse.data.order;
        }

        const orderId = createdOrder.id;
        if (!user) {
          saveGuestCheckoutContext({
            orderNumber: createdOrder.order_number || createdOrder.id,
            email: guestInfo.email,
          });
        }

        // Sepet/adres: 3D başarısız olursa "Tekrar dene" ile geri yüklenecek
        // Sepeti burada SİLME — yalnızca ödeme başarılı olunca temizlenir.
        savePendingCheckout({
          orderId,
          orderNumber: createdOrder.order_number || orderId,
          total: createdOrder.total,
          currency: createdOrder.currency || currency || 'TRY',
          paymentPending: true,
          items: (enrichedItems.length > 0 ? enrichedItems : items).map((item) => ({
            product_id: item.product_id,
            quantity: item.quantity,
            product: item.product || null,
            selected_variant: item.selected_variant || null,
          })),
          address: { ...address },
          guestInfo: user ? null : { ...guestInfo },
        });

        // Sipariş tutarları her zaman TRY saklanır — Ziraat'a doğrudan TRY gönder
        const totalTry = Number(createdOrder.total);

        const ziraatResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ziraat/initiate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            order_id: orderId,
            card_number: ziraatCard.number.replace(/\s/g, ''),
            card_holder: ziraatCard.holder,
            expiry_month: ziraatCard.expiryMonth,
            expiry_year: ziraatCard.expiryYear,
            cvv: ziraatCard.cvv,
            amount: totalTry,
            currency: 'TRY',
            installment: parseInt(ziraatCard.installment),
            customer_email: user?.email || guestInfo.email
          })
        });

        const ziraatData = await ziraatResponse.json();

        if (ziraatData.success && ziraatData.html_form) {
          // Open 3D Secure form in a new window or replace current page
          const newWindow = window.open('', '_self');
          newWindow.document.write(ziraatData.html_form);
          newWindow.document.close();
        } else {
          throw new Error(ziraatData.message || t('checkout.ziraatPaymentFailed'));
        }

        return;
      }

      // International products - use Revolut
      const checkoutMetaIntl = {
        currency: currency || 'TRY',
        checkout_locale: (i18n.language || 'tr').split('-')[0],
        checkout_shipping: shippingCost, // TRY
      };
      if (user) {
        // Logged in user checkout
        orderResponse = await orders.create({
          items: orderItems,
          shipping_address: address,
          payment_method: 'revolut',
          ...checkoutMetaIntl,
        });

        paymentResponse = await payments.checkout(
          orderResponse.data.id,
          window.location.origin
        );
      } else {
        // Guest checkout
        orderResponse = await orders.createGuest({
          items: orderItems,
          shipping_address: address,
          guest_email: guestInfo.email,
          guest_phone: guestInfo.phone || null,
          payment_method: 'revolut',
          ...checkoutMetaIntl,
        });

        paymentResponse = await payments.guestCheckout(
          orderResponse.data.order.id,
          guestInfo.email,
          window.location.origin
        );
      }

      // Sepeti burada silme — ödeme başarılı olunca temizlenir (retry için korunur)
      const revolutOrderId = orderResponse.data.id || orderResponse.data.order?.id;
      savePendingCheckout({
        orderId: revolutOrderId,
        orderNumber: orderResponse.data.order_number || orderResponse.data.order?.order_number || revolutOrderId,
        total: orderResponse.data.total || orderResponse.data.order?.total,
        currency: orderResponse.data.currency || orderResponse.data.order?.currency || currency || 'TRY',
        paymentPending: true,
        items: (enrichedItems.length > 0 ? enrichedItems : items).map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          product: item.product || null,
          selected_variant: item.selected_variant || null,
        })),
        address: { ...address },
        guestInfo: user ? null : { ...guestInfo },
      });

      // Redirect to Revolut checkout
      if (paymentResponse.data.checkout_url) {
        window.location.href = paymentResponse.data.checkout_url;
      } else {
        // Handle embedded checkout if no redirect URL
        toast.info(t('checkout.paymentPageLoading'));
        // Store order info for embedded checkout
        localStorage.setItem('pendingRevolutOrder', JSON.stringify({
          orderId: revolutOrderId,
          publicId: paymentResponse.data.public_id,
          amount: paymentResponse.data.amount,
          currency: paymentResponse.data.currency
        }));
        navigate(`/${i18n.language}/checkout/revolut`);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || error.message || t('checkout.checkoutFailed'));
      setLoading(false);
    }
  };

  // Kargo: lib/shippingQuote + hooks/useShipping (panel/ürün shipping_cost, yoksa 185 TR / 250 yurtdışı taban)

  // Toplam ağırlık hesaplama (kg)
  const calculateTotalWeight = () => {
    const itemsToCheck = enrichedItems.length > 0 ? enrichedItems : items;
    let totalWeightGrams = 0;
    itemsToCheck.forEach(item => {
      const weightGrams = item.product?.weight || 100; // Varsayılan 100g
      totalWeightGrams += weightGrams * item.quantity;
    });
    return totalWeightGrams / 1000; // kg'a çevir
  };
  
  const totalWeightKg = calculateTotalWeight();

  const cartTax = useMemo(() => {
    const list = enrichedItems.length > 0 ? enrichedItems : items;
    return computeCartTaxFromItems(list, getWholesalePrice, convertPrice);
  }, [items, enrichedItems, getWholesalePrice, convertPrice]);

  const intlCartTax = useMemo(
    () => computeCartTaxFromItems(cartAnalysis.internationalSellerItems, getWholesalePrice, convertPrice),
    [cartAnalysis.internationalSellerItems, getWholesalePrice, convertPrice]
  );

  const subtotalTRY = subtotalToTRY(cartTax.subtotal, currency, exchangeRates);
  const shippingDestination = guessShippingDestination({
    addressCountry: address.country,
    currency,
    countryCode,
  });
  const shippingCost = getCartShippingTRY({
    items: enrichedItems.length > 0 ? enrichedItems : items,
    destinationCountry: shippingDestination,
    subtotalTRY,
    totalWeightKg,
  });

  const tax = cartTax.totalVatDisplay;
  const shippingConverted = convertPrice(shippingCost, 'TRY');
  const total = cartTax.grandTotal + shippingConverted;
  const payableTotal = cartAnalysis.hasInternationalProducts
    ? intlCartTax.grandTotal + shippingConverted
    : total;

  const displayItems = enrichedItems.length > 0 ? enrichedItems : items;

  const savingsInfo = useMemo(() => {
    let compareSaving = 0;
    let wholesaleSaving = 0;
    displayItems.forEach((item) => {
      const p = item.product;
      if (!p) return;
      const qty = item.quantity || 1;
      const cur = productPriceCurrency(p);
      const paidUnit = getWholesalePrice(p, qty);
      const regularUnit = Number(p.price) || 0;
      const compareUnit = Number(p.compare_price || p.original_price) || 0;
      const paid = convertPrice(paidUnit, cur) * qty;
      const regular = convertPrice(regularUnit, cur) * qty;
      const compare = compareUnit > 0 ? convertPrice(compareUnit, cur) * qty : 0;
      if (compare > regular + 0.009) compareSaving += compare - regular;
      if (regular > paid + 0.009) wholesaleSaving += regular - paid;
    });
    const lines = [];
    if (compareSaving > 0.009) {
      lines.push({ key: 'compare', label: t('checkout.priceDiscount'), amount: compareSaving });
    }
    if (wholesaleSaving > 0.009) {
      lines.push({ key: 'wholesale', label: t('checkout.wholesaleSaving'), amount: wholesaleSaving });
    }
    const totalSaving = compareSaving + wholesaleSaving;
    const lowStockCount = displayItems.filter((item) => {
      const s = item.product?.stock;
      return s != null && s > 0 && s <= 3;
    }).length;
    return {
      lines,
      totalSaving,
      productsOriginal: cartTax.subtotal + totalSaving,
      lowStockCount,
    };
  }, [displayItems, getWholesalePrice, convertPrice, cartTax.subtotal, t]);

  const confirmDisabled =
    loading ||
    (cartAnalysis.onlyTurkishProducts &&
      paymentMethod === 'card' &&
      (!ziraatCard.holder || !ziraatCard.number || !ziraatCard.cvv));

  const isAddressComplete = Boolean(
    address.full_name && address.address_line1 && address.city && address.country && address.phone,
  );

  const confirmLabel =
    cartAnalysis.onlyTurkishProducts && paymentMethod === 'bank_transfer'
      ? t('checkout.confirmBankTransferOrder')
      : t('checkout.confirmOrder');

  const handleConfirmClick = () => {
    if (!user && !isGuestCheckout) {
      toast.error(t('checkout.selectGuestOrLogin'));
      return;
    }
    if (!validateAddressForm()) {
      setEditingAddress(true);
      return;
    }
    if (!privacyPolicyAccepted) {
      toast.error(t('checkout.privacyPolicyRequired'));
      document.getElementById('privacy-policy-after-address')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return;
    }
    handleSubmitOrder();
  };

  const summaryBreakdownProps = {
    t,
    formatPrice,
    currency,
    items: displayItems,
    getLocalizedProductName,
    productLangKey,
    savingsInfo,
    savingsOpen,
    setSavingsOpen,
    shippingConverted,
    shippingDestination,
    tax,
    totalWeightKg,
    privacyPolicyAccepted,
    setPrivacyPolicyAccepted,
  };

  const copyIban = async () => {
    try {
      await navigator.clipboard.writeText(BANK_TRANSFER_DETAILS.ibanCompact);
      toast.success(t('checkout.ibanCopied'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  // Havale/EFT onay ekranı
  if (bankTransferOrder) {
    return (
      <div className="py-16" data-testid="checkout-bank-transfer">
        <div className="container mx-auto px-4 md:px-8 max-w-xl">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
              <Banknote className="w-10 h-10 text-amber-700" />
            </div>
            <h1 className="text-3xl font-serif font-bold mb-3">{t('checkout.bankTransferTitle')}</h1>
            <p className="text-muted-foreground">{t('checkout.bankTransferSubtitle')}</p>
          </div>

          <div className="border rounded-xl p-6 bg-muted/30 space-y-4 mb-6">
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">{t('checkout.orderNumber')}</span>
              <span className="font-mono font-semibold">{bankTransferOrder.orderNumber}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">{t('checkout.paidAmount')}</span>
              <span className="font-semibold">{formatPrice(bankTransferOrder.total)}</span>
            </div>
            <Separator />
            <div>
              <p className="text-xs text-muted-foreground mb-1">{t('checkout.bankName')}</p>
              <p className="font-medium">{BANK_TRANSFER_DETAILS.bankName}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">{t('checkout.accountName')}</p>
              <p className="font-medium">{BANK_TRANSFER_DETAILS.accountName}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">IBAN</p>
              <div className="flex items-center gap-2">
                <p className="font-mono text-sm font-medium">{BANK_TRANSFER_DETAILS.iban}</p>
                <Button type="button" variant="outline" size="sm" onClick={copyIban} className="shrink-0 h-8">
                  <Copy className="w-3.5 h-3.5 mr-1" />
                  {t('checkout.copyIban')}
                </Button>
              </div>
            </div>
            <Alert className="border-amber-200 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 text-sm">
                {t('checkout.bankTransferReference', { orderNumber: bankTransferOrder.orderNumber })}
              </AlertDescription>
            </Alert>
          </div>

          <p className="text-sm text-muted-foreground text-center mb-6">{t('checkout.bankTransferPendingNote')}</p>

          <div className="space-y-3">
            {user ? (
              <Button className="w-full rounded-full" onClick={() => navigate(`/${i18n.language}/orders`)}>
                {t('checkout.viewOrders')}
              </Button>
            ) : (
              <Button
                className="w-full rounded-full"
                onClick={() => navigate(buildGuestOrderTrackPath({
                  orderNumber: bankTransferOrder.orderNumber,
                  email: guestInfo.email,
                  lang: i18n.language,
                }))}
              >
                {t('checkout.viewOrder')}
              </Button>
            )}
            <Button variant="outline" className="w-full rounded-full" onClick={() => navigate(`/${i18n.language}/products`)}>
              {t('cart.continueShopping')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Payment success view
  if (paymentStatus === 'success') {
    return (
      <div className="py-16" data-testid="checkout-success">
        <div className="container mx-auto px-4 md:px-8 text-center max-w-lg">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-3xl font-serif font-bold mb-4">{t("checkout.orderConfirmed")}</h1>
          <p className="text-muted-foreground mb-8">
            {t("checkout.thankYou")}
          </p>
          <div className="space-y-3">
            {user ? (
              <Button className="w-full rounded-full" onClick={() => navigate('/orders')} data-testid="view-orders-btn">
                {t("checkout.viewOrders")}
              </Button>
            ) : (
              <Button
                className="w-full rounded-full"
                onClick={() => navigate(buildGuestOrderTrackPath({
                  orderNumber: readGuestCheckoutContext().orderNumber,
                  email: readGuestCheckoutContext().email || guestInfo.email,
                  lang: i18n.language,
                }))}
                data-testid="view-orders-btn"
              >
                {t('checkout.viewOrder')}
              </Button>
            )}
            <Button variant="outline" className="w-full rounded-full" onClick={() => navigate('/products')}>
              {t("cart.continueShopping")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0 && !sessionId) {
    const pending = readPendingCheckout();
    // pending varsa boş sepete atma — geri yükleme bitsin / kullanıcı manuel denesin
    if (cartLoading || !cartRestoreDone || pending?.items?.length) {
      return (
        <div className="min-h-[40vh] flex flex-col items-center justify-center gap-4 px-4">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          {cartRestoreDone && pending?.items?.length ? (
            <Button
              type="button"
              variant="outline"
              onClick={async () => {
                await restoreCartItems(pending.items);
              }}
            >
              {t('checkout.restoreCart', 'Sepeti geri yükle')}
            </Button>
          ) : null}
        </div>
      );
    }
    navigate('/cart');
    return null;
  }

  const addressTitle = [address.state, address.city].filter(Boolean).join(' / ')
    || address.full_name
    || t('checkout.shippingAddress');
  const addressLinePreview = [address.address_line1, address.address_line2].filter(Boolean).join(', ');
  const showAddressForm = editingAddress || !isAddressComplete;

  return (
    <div className="bg-[#F5F5F5] min-h-screen pb-28 lg:pb-10" data-testid="checkout-page">
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 h-12 flex items-center justify-center relative">
          <button
            type="button"
            onClick={() => navigate('/cart')}
            className="absolute left-3 sm:left-4 p-1 text-gray-700"
            aria-label={t('checkout.backToCart')}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#FF6000]" />
            <span className="text-sm font-medium text-gray-800">{t('checkout.paySecurely')}</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto sm:px-4 py-0 sm:py-4">
        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-4 lg:items-start">
          <div className="space-y-3">
            <div className="bg-white sm:rounded-xl overflow-hidden shadow-sm border-y sm:border border-gray-100" data-testid="shipping-form">
              <CheckoutSectionBand>{t('checkout.shippingAddress')}</CheckoutSectionBand>
              <div className="p-4 space-y-4">
                {!user && (
                  <div className="rounded-lg border border-gray-200 p-4 bg-gray-50">
                    <h3 className="text-sm font-semibold mb-3">{t('checkout.howToContinue')}</h3>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div
                        className={`border rounded-lg p-3 cursor-pointer transition-all ${
                          isGuestCheckout
                            ? 'border-[#FF6000] bg-orange-50/50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                        onClick={() => setIsGuestCheckout(true)}
                        data-testid="guest-checkout-option"
                      >
                        <div className="flex items-center gap-3 mb-1">
                          <OrangeRadio selected={isGuestCheckout} />
                          <span className="font-medium text-sm">{t('checkout.guestCheckout')}</span>
                        </div>
                        <p className="text-xs text-muted-foreground ml-8">
                          {t('checkout.guestCheckoutDesc')}
                        </p>
                      </div>

                      <div
                        className="border border-gray-200 bg-white hover:border-gray-300 rounded-lg p-3 cursor-pointer transition-all"
                        onClick={() => navigate('/login?redirect=/checkout')}
                        data-testid="login-checkout-option"
                      >
                        <div className="flex items-center gap-3 mb-1">
                          <OrangeRadio selected={false} />
                          <span className="font-medium text-sm">{t('checkout.loginRegister')}</span>
                        </div>
                        <p className="text-xs text-muted-foreground ml-8">
                          {t('checkout.loginRegisterDesc')}
                        </p>
                      </div>
                    </div>

                    {isGuestCheckout && (
                      <div className="mt-4 pt-4 border-t space-y-3">
                        <div>
                          <Label htmlFor="guest_email" className="flex items-center gap-1 text-sm">
                            {t('checkout.emailAddress')} <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="guest_email"
                            type="email"
                            value={guestInfo.email}
                            onChange={(e) => handleGuestInfoChange('email', e.target.value)}
                            placeholder={t('checkout.emailPlaceholder')}
                            className="mt-1"
                            data-testid="input-guest-email"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            {t('checkout.orderConfirmationEmail')}
                          </p>
                        </div>
                        <div>
                          <Label htmlFor="guest_phone" className="text-sm">{t('checkout.mobilePhone')} ({t('checkout.required')}) <span className="text-red-500">*</span></Label>
                          <Input
                            id="guest_phone"
                            type="tel"
                            value={guestInfo.phone}
                            onChange={(e) => handleGuestInfoChange('phone', e.target.value)}
                            placeholder={t('checkout.phonePlaceholderTr')}
                            className="mt-1"
                            required
                            data-testid="input-guest-phone"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            {t('checkout.orderNotifications')}
                          </p>
                          {!guestInfo.phone && <p className="text-xs text-red-500 mt-1">{t('checkout.phoneRequired')}</p>}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <label className="flex items-center gap-3 cursor-pointer">
                  <OrangeRadio selected />
                  <span className="text-sm font-medium text-gray-900">{t('checkout.sendToMyAddress')}</span>
                </label>

                {!showAddressForm ? (
                  <div className="flex items-start gap-3 border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{addressTitle}</p>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                        {addressLinePreview}
                        {address.full_name ? ` · ${address.full_name}` : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEditingAddress(true)}
                      className="text-sm font-semibold text-[#FF6000] shrink-0"
                    >
                      {t('checkout.changeAddress')}
                    </button>
                  </div>
                ) : null}

                {showAddressForm ? (
                <>
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Ülke Seçimi - İLK SIRADA - Aranabilir */}
                  <div className="md:col-span-2">
                    <Label htmlFor="country">{t("checkout.country")} <span className="text-red-500">*</span></Label>
                    <Popover open={countryOpen} onOpenChange={setCountryOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={countryOpen}
                          className="w-full justify-between mt-1 font-normal"
                          data-testid="select-country"
                        >
                          {selectedCountry
                            ? `${selectedCountry.flag} ${selectedCountry.name}`
                            : t('checkout.selectCountry')}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0" align="start">
                        <Command>
                          <CommandInput placeholder={t('checkout.searchCountry')} />
                          <CommandList>
                            <CommandEmpty>{t('checkout.countryNotFound')}</CommandEmpty>
                            <CommandGroup>
                              {localizedCountries.map((country) => (
                                <CommandItem
                                  key={country.code}
                                  value={`${country.name} ${country.code}`}
                                  onSelect={() => {
                                    handleAddressChange('country', country.code);
                                    // Ülke değiştiğinde şehri sıfırla
                                    handleAddressChange('city', '');
                                    setCountryOpen(false);
                                  }}
                                >
                                  <Check
                                    className={`mr-2 h-4 w-4 ${address.country === country.code ? "opacity-100" : "opacity-0"}`}
                                  />
                                  {country.flag} {country.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Ad Soyad */}
                  <div className="md:col-span-2">
                    <Label htmlFor="full_name">{t("checkout.fullName")} <span className="text-red-500">*</span></Label>
                    <Input
                      id="full_name"
                      value={address.full_name}
                      onChange={(e) => handleAddressChange('full_name', e.target.value)}
                      placeholder={t('checkout.fullName')}
                      className="mt-1"
                      data-testid="input-full-name"
                    />
                  </div>

                  {/* Cep Telefonu - ZORUNLU */}
                  <div className="md:col-span-2">
                    <Label htmlFor="phone">{t('checkout.mobilePhone')} ({t('checkout.required')}) <span className="text-red-500">*</span></Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={address.phone}
                      onChange={(e) => handleAddressChange('phone', e.target.value)}
                      placeholder={address.country === 'TR' ? t('checkout.phonePlaceholderTr') : t('checkout.phonePlaceholderIntl')}
                      className="mt-1"
                      required
                      data-testid="input-phone"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('checkout.orderNotifications')}
                    </p>
                    {!address.phone && <p className="text-xs text-red-500 mt-1">{t('checkout.phoneRequired')}</p>}
                  </div>

                  {/* Adres Satırı 1 */}
                  <div className="md:col-span-2">
                    <Label htmlFor="address_line1">
                      {address.country === 'TR' ? t('checkout.addressTrLine1') : t("checkout.addressLine1")} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="address_line1"
                      value={address.address_line1}
                      onChange={(e) => handleAddressChange('address_line1', e.target.value)}
                      placeholder={address.country === 'TR' ? t('checkout.addressTrLine1Placeholder') : t('checkout.streetAddressPlaceholder')}
                      className="mt-1"
                      data-testid="input-address1"
                    />
                  </div>

                  {/* Adres Satırı 2 */}
                  <div className="md:col-span-2">
                    <Label htmlFor="address_line2">
                      {address.country === 'TR' ? t('checkout.addressTrLine2') : t("checkout.addressLine2")}
                    </Label>
                    <Input
                      id="address_line2"
                      value={address.address_line2}
                      onChange={(e) => handleAddressChange('address_line2', e.target.value)}
                      placeholder={address.country === 'TR' ? t('checkout.addressTrLine2Placeholder') : t('checkout.apartmentPlaceholder')}
                      className="mt-1"
                      data-testid="input-address2"
                    />
                  </div>

                  {/* Şehir / İlçe - Ülkeye göre değişir */}
                  <div>
                    <Label htmlFor="city">
                      {address.country === 'TR' ? t('checkout.cityTr') : t("checkout.city")} <span className="text-red-500">*</span>
                    </Label>
                    {address.country === 'TR' ? (
                      <Select value={address.city} onValueChange={(v) => handleAddressChange('city', v)}>
                        <SelectTrigger className="mt-1" data-testid="input-city">
                          <SelectValue placeholder={t('checkout.selectCityTr')} />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {TURKEY_CITIES.map((city) => (
                            <SelectItem key={city} value={city}>{city}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id="city"
                        value={address.city}
                        onChange={(e) => handleAddressChange('city', e.target.value)}
                        placeholder={t('checkout.city')}
                        className="mt-1"
                        data-testid="input-city"
                      />
                    )}
                  </div>

                  {/* İlçe / State */}
                  <div>
                    <Label htmlFor="state">
                      {address.country === 'TR' ? t('checkout.districtTr') : t("checkout.state")} {address.country === 'TR' && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      id="state"
                      value={address.state}
                      onChange={(e) => handleAddressChange('state', e.target.value)}
                      placeholder={address.country === 'TR' ? t('checkout.districtPlaceholderTr') : t('checkout.stateProvincePlaceholder')}
                      className="mt-1"
                      data-testid="input-state"
                    />
                  </div>

                  {/* Posta Kodu */}
                  <div className="md:col-span-2">
                    <Label htmlFor="postal_code">{t("checkout.postalCode")} {address.country !== 'TR' && <span className="text-red-500">*</span>}</Label>
                    <Input
                      id="postal_code"
                      value={address.postal_code}
                      onChange={(e) => handleAddressChange('postal_code', e.target.value)}
                      placeholder={address.country === 'TR' ? t('checkout.postalOptionalTr') : t('checkout.postalCode')}
                      className="mt-1"
                      data-testid="input-postal"
                    />
                  </div>
                </div>

                <Button
                  className="w-full md:w-auto rounded-lg mt-2 bg-[#FF6000] hover:bg-[#e55700] text-white"
                  onClick={() => {
                    if (!user && !isGuestCheckout) {
                      toast.error(t('checkout.selectGuestOrLogin'));
                      return;
                    }
                    if (!user && isGuestCheckout && !validateGuestInfo()) {
                      return;
                    }
                    if (!validateAddressForm()) return;
                    setEditingAddress(false);
                    setStep(2);
                  }}
                  disabled={!address.full_name || !address.address_line1 || !address.city}
                  data-testid="continue-to-payment"
                >
                  {t('checkout.saveAddress')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                </>
                ) : null}

                <button
                  type="button"
                  onClick={() => setInvoiceOpen((v) => !v)}
                  className="flex items-center gap-1 text-xs text-gray-500"
                >
                  <span className="text-[#FF6000] font-semibold">+</span>
                  {t('checkout.invoiceInfo')}: {addressTitle}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${invoiceOpen ? 'rotate-180' : ''}`} />
                </button>
                {invoiceOpen && (
                  <p className="text-xs text-gray-500 pl-4">
                    {address.full_name}
                    {addressLinePreview ? ` — ${addressLinePreview}` : ''}
                  </p>
                )}
              </div>
            </div>

            <div className="bg-white sm:rounded-xl overflow-hidden shadow-sm border-y sm:border border-gray-100 lg:hidden">
              <CheckoutLegalConsent
                t={t}
                privacyPolicyAccepted={privacyPolicyAccepted}
                setPrivacyPolicyAccepted={setPrivacyPolicyAccepted}
                checkboxId="privacy-policy-after-address"
              />
            </div>

            <div className="bg-white sm:rounded-xl overflow-hidden shadow-sm border-y sm:border border-gray-100" data-testid="payment-form">
              <CheckoutSectionBand>{t('checkout.paymentOptions')}</CheckoutSectionBand>
              <div className="p-4 space-y-4">

                {/* Mixed Cart Warning */}
                {cartAnalysis.mixedCart && (
                  <Alert className="border-amber-200 bg-amber-50">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertTitle className="text-amber-800">{t('checkout.mixedCartTitle')}</AlertTitle>
                    <AlertDescription className="text-amber-700">
                      {t('checkout.mixedCartDesc', { amount: formatPrice(cartAnalysis.internationalTotal) })}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Only Turkish Products - payment method choice */}
                {cartAnalysis.onlyTurkishProducts && (
                  <div className="space-y-4">
                    <div>
                      <div className="space-y-0 divide-y">
                        <button
                          type="button"
                          onClick={() => setPaymentMethod('card')}
                          className="w-full text-left py-3 flex items-center gap-3"
                          data-testid="payment-method-card"
                        >
                          <OrangeRadio selected={paymentMethod === 'card'} />
                          <CreditCard className={`w-5 h-5 ${paymentMethod === 'card' ? 'text-[#FF6000]' : 'text-gray-400'}`} />
                          <div>
                            <p className="font-semibold text-sm">{t('checkout.payWithCard')}</p>
                            <p className="text-xs text-muted-foreground">{t('checkout.payWithCardDesc')}</p>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setPaymentMethod('bank_transfer')}
                          className="w-full text-left py-3 flex items-center gap-3"
                          data-testid="payment-method-bank-transfer"
                        >
                          <OrangeRadio selected={paymentMethod === 'bank_transfer'} />
                          <Banknote className={`w-5 h-5 ${paymentMethod === 'bank_transfer' ? 'text-[#FF6000]' : 'text-gray-400'}`} />
                          <div>
                            <p className="font-semibold text-sm">{t('checkout.payWithBankTransfer')}</p>
                            <p className="text-xs text-muted-foreground">{t('checkout.payWithBankTransferDesc')}</p>
                          </div>
                        </button>
                      </div>
                    </div>

                    {paymentMethod === 'bank_transfer' ? (
                      <div className="border-2 border-amber-200 rounded-xl p-6 bg-amber-50/50" data-testid="bank-transfer-details">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                            <Building2 className="w-6 h-6 text-amber-700" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-amber-900">{t('checkout.bankTransferDetailsTitle')}</h3>
                            <p className="text-sm text-amber-700">{t('checkout.bankTransferDetailsDesc')}</p>
                          </div>
                        </div>
                        <div className="space-y-3 text-sm bg-white/70 rounded-lg p-4 border border-amber-100">
                          <div className="flex justify-between gap-4">
                            <span className="text-muted-foreground shrink-0">{t('checkout.bankName')}</span>
                            <span className="font-medium text-right">{BANK_TRANSFER_DETAILS.bankName}</span>
                          </div>
                          <div className="flex justify-between gap-4">
                            <span className="text-muted-foreground shrink-0">{t('checkout.accountName')}</span>
                            <span className="font-medium text-right">{BANK_TRANSFER_DETAILS.accountName}</span>
                          </div>
                          <div className="flex justify-between gap-4 items-start">
                            <span className="text-muted-foreground shrink-0">IBAN</span>
                            <div className="text-right">
                              <span className="font-mono font-medium block">{BANK_TRANSFER_DETAILS.iban}</span>
                              <Button type="button" variant="ghost" size="sm" onClick={copyIban} className="h-7 mt-1 text-xs">
                                <Copy className="w-3 h-3 mr-1" />
                                {t('checkout.copyIban')}
                              </Button>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-amber-800 mt-4">{t('checkout.bankTransferCheckoutNote')}</p>
                      </div>
                    ) : (
                  <div className="border-2 border-green-200 rounded-xl p-6 bg-green-50/50">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-green-800">{t('checkout.ziraatTitle')}</h3>
                        <p className="text-sm text-green-600">{t('checkout.ziraatSubtitle')}</p>
                      </div>
                    </div>
                    
                    {/* Credit Card Form for Ziraat */}
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="ziraat_card_holder">{t('checkout.cardHolderName')}</Label>
                        <Input 
                          id="ziraat_card_holder"
                          placeholder={t('checkout.cardHolderPlaceholder')}
                          value={ziraatCard?.holder || ''}
                          onChange={(e) => setZiraatCard({...ziraatCard, holder: e.target.value.toUpperCase()})}
                          className="mt-1"
                          data-testid="ziraat-card-holder"
                        />
                      </div>
                      <div>
                        <Label htmlFor="ziraat_card_number">{t('checkout.cardNumber')}</Label>
                        <Input 
                          id="ziraat_card_number"
                          placeholder="1234 5678 9012 3456"
                          value={ziraatCard?.number || ''}
                          onChange={(e) => {
                            let value = e.target.value.replace(/\D/g, '').slice(0, 16);
                            value = value.replace(/(\d{4})(?=\d)/g, '$1 ');
                            setZiraatCard({...ziraatCard, number: value});
                          }}
                          className="mt-1"
                          data-testid="ziraat-card-number"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="ziraat_expiry_month">{t('checkout.expiryMonth')}</Label>
                          <select
                            id="ziraat_expiry_month"
                            value={ziraatCard?.expiryMonth || ''}
                            onChange={(e) => setZiraatCard({...ziraatCard, expiryMonth: e.target.value})}
                            className="w-full border rounded-md px-3 py-2 mt-1"
                            data-testid="ziraat-expiry-month"
                          >
                            <option value="">AA</option>
                            {Array.from({length: 12}, (_, i) => (i + 1).toString().padStart(2, '0')).map(m => (
                              <option key={m} value={m}>{m}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label htmlFor="ziraat_expiry_year">{t('checkout.expiryYear')}</Label>
                          <select
                            id="ziraat_expiry_year"
                            value={ziraatCard?.expiryYear || ''}
                            onChange={(e) => setZiraatCard({...ziraatCard, expiryYear: e.target.value})}
                            className="w-full border rounded-md px-3 py-2 mt-1"
                            data-testid="ziraat-expiry-year"
                          >
                            <option value="">YY</option>
                            {Array.from({length: 10}, (_, i) => (new Date().getFullYear() + i).toString().slice(-2)).map(y => (
                              <option key={y} value={y}>{y}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label htmlFor="ziraat_cvv">CVV</Label>
                          <Input 
                            id="ziraat_cvv"
                            type="password"
                            placeholder="***"
                            maxLength={4}
                            value={ziraatCard?.cvv || ''}
                            onChange={(e) => setZiraatCard({...ziraatCard, cvv: e.target.value.replace(/\D/g, '').slice(0, 4)})}
                            className="mt-1"
                            data-testid="ziraat-cvv"
                          />
                        </div>
                      </div>
                      
                      {/* Installment Options */}
                      <div>
                        <Label>{t('checkout.installmentOptions')}</Label>
                        <select
                          value={ziraatCard?.installment || '1'}
                          onChange={(e) => setZiraatCard({...ziraatCard, installment: e.target.value})}
                          className="w-full border rounded-md px-3 py-2 mt-1"
                          data-testid="ziraat-installment"
                        >
                          <option value="1">{t('checkout.installmentSingle', { amount: formatPrice(total, currency) })}</option>
                          {[2, 3, 6, 9, 12].map((n) => {
                            const rates = { 2: 1.015, 3: 1.025, 6: 1.045, 9: 1.065, 12: 1.085 };
                            const monthly = formatPrice((total * rates[n]) / n, currency);
                            return (
                              <option key={n} value={String(n)}>
                                {t('checkout.installmentN', { count: n, amount: monthly, perMonth: t('checkout.perMonth') })}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>
                    
                    <PaymentBrandLogos
                      className="mt-4 pt-4 border-t"
                      bankLabel={t('checkout.securePayment3d')}
                    />
                  </div>
                    )}
                  </div>
                )}

                {/* International Products - Revolut Payment */}
                {cartAnalysis.hasInternationalProducts && (
                  <div className="border rounded-xl p-6 bg-blue-50/50">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <CreditCard className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-blue-800">{t('checkout.revolutTitle')}</h3>
                        <p className="text-sm text-blue-600">{t('checkout.revolutSubtitle', { amount: formatPrice(cartAnalysis.internationalTotal) })}</p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      {t('checkout.revolutRedirectNote')}
                    </p>
                    <PaymentBrandLogos
                      size="sm"
                      showRevolut
                      className="gap-2"
                      bankLabel={t('checkout.securePayment3d')}
                    />
                  </div>
                )}

                {/* Turkish Products in Cart Summary */}
                {cartAnalysis.hasTurkishProducts && !cartAnalysis.onlyTurkishProducts && (
                  <div className="border rounded-xl p-4 bg-gray-50">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">🇹🇷</span>
                      <span className="font-medium">{t('checkout.turkishSellers')}</span>
                      <span className="text-sm text-muted-foreground">({t('checkout.productsCount', { count: cartAnalysis.turkishSellerItems.length })})</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {t('checkout.turkishSellersPending', { amount: formatPrice(cartAnalysis.turkishTotal) })}
                    </p>
                  </div>
                )}

              </div>
            </div>
          </div>

          <CheckoutDesktopSidebar
            t={t}
            confirmLabel={confirmLabel}
            onConfirm={handleConfirmClick}
            loading={loading}
            disabled={confirmDisabled}
            savingsInfo={savingsInfo}
            {...summaryBreakdownProps}
          />
        </div>
      </div>

      {mobileSummaryOpen && (
        <div className="fixed bottom-[72px] left-0 right-0 z-40 lg:hidden max-h-[65vh] overflow-y-auto rounded-t-2xl shadow-2xl">
          <CheckoutSummaryBreakdown
            {...summaryBreakdownProps}
            showLegal={false}
            onClose={() => setMobileSummaryOpen(false)}
          />
        </div>
      )}

      <CheckoutStickyBar
        t={t}
        formatPrice={formatPrice}
        currency={currency}
        total={payableTotal}
        savingsInfo={savingsInfo}
        mobileSummaryOpen={mobileSummaryOpen}
        setMobileSummaryOpen={setMobileSummaryOpen}
        onConfirm={handleConfirmClick}
        confirmLabel={confirmLabel}
        loading={loading}
        disabled={confirmDisabled}
      />
    </div>
  );
};

export default CheckoutPage;
